import { SubscriptionCard } from '@/components/molecules';
import { PricingSection } from './pricing-section';
import { CancelSubscriptionModal } from './cancel-subscription-modal';
import { useEffect, useState } from 'react';
import { useQueryState } from 'nuqs';
import {
  Badge,
  Button,
  Heading,
  PageLoader,
  Paragraph,
} from '@/components/atoms';
import { routes, sampleFeatures } from '@/lib/constants';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  billingKeys,
  useBusinessSubscription,
  useCancelSubscription,
} from '@/api';
import { formatCurrency } from '@/lib/helpers/format-currency';
import {
  getBillingIntervalLabel,
  getPlanCardVariant,
  getPlanDisplayName,
  getPlanPlatformFeePercent,
  parsePlanFeature,
} from '@/lib/helpers/subscription-plan';
import type { BusinessSubscriptionPlan } from '@/types';
import { Check } from 'lucide-react';

const FREEMIUM_PLAN: BusinessSubscriptionPlan = {
  id: 0,
  providerPlanId: '',
  provider: 'paystack',
  billingInterval: 'monthly',
  currencyCode: 'NGN',
  price: '0',
  package: 'freemium',
  name: 'Starter',
  description: 'Everything you need to start selling smarter through DMs.',
  features: sampleFeatures,
  createdAt: '',
  updatedAt: '',
  deletedAt: null,
};

const formatPrice = (plan: BusinessSubscriptionPlan) => {
  const amount = parseFloat(plan.price ?? '0');
  if (!amount) return 'Free';

  return formatCurrency(amount, {
    currency: plan.currencyCode ?? 'NGN',
  });
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const Plans = () => {
  const [view] = useQueryState('view');
  const [showOptions, setShowOptions] = useState(view === 'plans');

  useEffect(() => {
    if (view === 'plans') setShowOptions(true);
  }, [view]);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: subscription, isLoading } = useBusinessSubscription();

  const { mutate: cancelSubscription, isPending: isCanceling } =
    useCancelSubscription(() => {
      setShowCancelModal(false);
      queryClient.invalidateQueries({ queryKey: billingKeys.business() });
    });

  if (isLoading) {
    return (
      <PageLoader message="Loading subscription details..." className="py-20" />
    );
  }

  const plan = subscription?.subscriptionPlan ?? FREEMIUM_PLAN;
  const planName = getPlanDisplayName(plan);
  const intervalLabel = getBillingIntervalLabel(plan.billingInterval, 'long');
  const features = plan.features?.length ? plan.features : sampleFeatures;
  const amount = Number.parseFloat(plan.price ?? '0');
  const isFreemium =
    plan.package === 'freemium' || plan.package === 'starter' || !amount;
  /** Scale is the top tier; every other package can still upgrade. */
  const canUpgrade = plan.package !== 'scale';
  const subscriptionStatus = subscription?.status;
  const canCancel =
    !!subscription &&
    !isFreemium &&
    !subscription.cancelledAt &&
    subscriptionStatus !== 'cancelled' &&
    subscriptionStatus !== 'expired';

  const renewalDate = formatDate(subscription?.nextPaymentDate);
  const periodEnd = formatDate(subscription?.endDate);
  const platformFee = getPlanPlatformFeePercent(plan);

  return (
    <div className="flex max-w-6xl flex-col items-start gap-10">
      <div className="flex w-full items-center justify-between gap-4">
        {showOptions ? (
          <Button
            variant="link"
            className="w-fit px-0"
            onClick={() => setShowOptions(false)}
          >
            Back to current plan
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="link"
          className="w-fit px-0"
          onClick={() => navigate(routes.billing)}
        >
          View history
        </Button>
      </div>

      {!showOptions ? (
        <div className="flex w-full gap-6 overflow-hidden max-lg:flex-col lg:justify-between">
          <div className="flex max-w-xl flex-col items-start gap-6">
            <div className="space-y-3">
              <Heading
                size="2xl"
                className="max-w-sm leading-relaxed wrap-break-word"
              >
                You are currently on{' '}
                <span className="text-primary">
                  {planName}
                  {!isFreemium ? ` (${intervalLabel})` : ''}
                </span>
              </Heading>
              <Paragraph className="text-sm text-muted-foreground">
                {plan.description}
              </Paragraph>
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                  platformFee === 0
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-amber-50 text-amber-800'
                }`}
              >
                {platformFee === 0
                  ? '0% platform fee'
                  : `${platformFee}% platform fee on sales`}
              </span>
            </div>

            {subscription && (
              <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                <Paragraph size="sm">
                  Status:{' '}
                  <span className="font-medium capitalize text-foreground">
                    {subscription.status}
                  </span>
                </Paragraph>
                {periodEnd && (
                  <Paragraph size="sm">
                    Current period ends:{' '}
                    <span className="font-medium text-foreground">
                      {periodEnd}
                    </span>
                  </Paragraph>
                )}
                {renewalDate && subscriptionStatus === 'active' && (
                  <Paragraph size="sm">
                    Next payment:{' '}
                    <span className="font-medium text-foreground">
                      {renewalDate}
                    </span>
                  </Paragraph>
                )}
              </div>
            )}

            <ul className="space-y-2.5">
              {features.map((feature, index) => {
                const parsed = parsePlanFeature(feature);
                if (!parsed.label || parsed.isGroupHeader) {
                  return parsed.isGroupHeader ? (
                    <li
                      key={`${parsed.label}-${index}`}
                      className="pt-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                    >
                      {parsed.label.replace(/:$/, '')}
                    </li>
                  ) : null;
                }

                return (
                  <li
                    key={`${parsed.label}-${index}`}
                    className="flex items-start gap-2.5 text-sm"
                  >
                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                      <Check className="size-3" strokeWidth={3} />
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      {parsed.label}
                      {parsed.badge === 'new' && (
                        <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50">
                          New
                        </Badge>
                      )}
                      {parsed.badge === 'coming-soon' && (
                        <Badge className="bg-amber-50 text-amber-800 hover:bg-amber-50">
                          Coming soon
                        </Badge>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="flex flex-wrap gap-2">
              {canUpgrade && (
                <Button onClick={() => setShowOptions(true)}>
                  Upgrade now
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(true)}
                >
                  Cancel plan
                </Button>
              )}
            </div>
          </div>

          <SubscriptionCard
            planName={`${planName} Plan`}
            description={plan.description}
            price={formatPrice(plan)}
            period={isFreemium ? 'forever' : intervalLabel}
            variant={getPlanCardVariant(plan.package)}
          />
        </div>
      ) : (
        <PricingSection />
      )}

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={() => cancelSubscription()}
        isLoading={isCanceling}
      />
    </div>
  );
};
